import {
  HomeOutlined,
  EnvironmentOutlined,
  NotificationOutlined,
  DeliveredProcedureOutlined,
  AlertOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { SidebarItem } from '_/components/common/layout/AppSidebar';
import { useFlowContext } from '_/contexts/FlowContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useFlowDeliveries } from '_/hooks/useFlows';
import React, { useMemo } from 'react';

// Create icon elements once to prevent re-renders
const homeIcon = <HomeOutlined />;
const environmentIcon = <EnvironmentOutlined />;
const notificationIcon = <NotificationOutlined />;
const deliveredProcedureIcon = <DeliveredProcedureOutlined />;
const alertIcon = <AlertOutlined />;
const settingIcon = <SettingOutlined />;

export function useFlowSidebarItems(): SidebarItem[] {
  const { selectedOrgSlug } = useOrganization();
  const { selectedProjectSlug } = useProject();
  const { flowSlug, flowId } = useFlowContext();
  const { data: flowDeliveriesData } = useFlowDeliveries(flowId);
  const flowDeliveries = flowDeliveriesData?.items || [];

  // Extract stable values for memoization
  const latestDeliveryId = flowDeliveries[0]?.id;

  const items = useMemo(() => {
    const baseItems: SidebarItem[] = [
      {
        icon: homeIcon,
        text: 'Flow Home',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}`,
      },
      {
        icon: environmentIcon,
        text: 'Destinations',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}/destinations`,
      },
    ];

    // Add "Latest Delivery" link if there are deliveries
    // Deliveries are sorted by addedAt DESC, so first item is the latest
    if (latestDeliveryId) {
      baseItems.push({
        icon: notificationIcon,
        text: 'Latest Delivery',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}/deliveries/${latestDeliveryId}`,
        status: 'external',
      });
    }

    baseItems.push(
      {
        icon: deliveredProcedureIcon,
        text: 'Deliveries',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}/deliveries`,
      },
      {
        icon: alertIcon,
        text: 'Alarm Groups',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}/alarmgroups`,
      },
      {
        icon: settingIcon,
        text: 'Settings',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/flows/${flowSlug}/settings`,
      },
    );

    return baseItems;
  }, [selectedOrgSlug, selectedProjectSlug, flowSlug, latestDeliveryId]);

  return items;
}
